import React, { useState, useEffect } from 'react';
import { RefreshCw, AlertCircle, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { formatDateTime } from '../../utils/dateUtils';

interface WebhookLog {
  _id: string;
  accountId: {
    _id: string;
    name: string;
  };
  tenantId: string;
  eventCategory: string;
  eventType: string;
  resourceId: string;
  eventDateUtc: string;
  payload: any;
  signature: string;
  isValid: boolean;
  processedAt: string;
  error?: string;
  createdAt: string;
}

interface Pagination {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export const WebhookLogs: React.FC = () => {
  const [logs, setLogs] = useState<WebhookLog[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchLogs();
  }, [filterCategory, filterType]);

  const fetchLogs = async (offset: number = 0) => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        limit: pagination.limit.toString(),
        offset: offset.toString()
      });

      if (filterCategory) params.append('eventCategory', filterCategory);
      if (filterType) params.append('eventType', filterType);
      
      const response = await fetch(`${apiUrl}/api/xero/webhook-logs?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      
      if (data.success) {
        setLogs(data.data.logs);
        setPagination(data.data.pagination);
      } else {
        setError(data.message || 'Failed to fetch webhook logs');
      }
    } catch (err) {
      console.error('Fetch webhook logs error:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return formatDateTime(dateString);
  };

  const formatDateWithUTC = (dateString: string) => {
    const date = new Date(dateString);
    const localTime = date.toLocaleString();
    const utcTime = date.toUTCString();
    return { localTime, utcTime };
  };

  const toggleExpanded = (logId: string) => {
    setExpandedLog(expandedLog === logId ? null : logId);
  };

  const getEventBadgeColor = (category: string) => {
    switch (category) {
      case 'CONTACT':
        return 'bg-accent-100 text-accent-800 dark:bg-accent-900/30 dark:text-accent-400';
      case 'INVOICE':
        return 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400';
      case 'EMPLOYEE':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'TAXRATE':
        return 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Xero Webhook Logs
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Real-time webhook events from Xero
          </p>
        </div>
        
        <button
          onClick={() => fetchLogs(0)}
          disabled={loading}
          className="flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 flex gap-4">
        <div>
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">
            Event Category
          </label>
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-500 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
          >
            <option value="">All Categories</option>
            <option value="CONTACT">CONTACT</option>
            <option value="INVOICE">INVOICE</option>
            <option value="EMPLOYEE">EMPLOYEE</option>
            <option value="TAXRATE">TAXRATE</option>
          </select>
        </div>

        <div>
          <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">
            Event Type
          </label>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-500 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
          >
            <option value="">All Types</option>
            <option value="CREATE">CREATE</option>
            <option value="UPDATE">UPDATE</option>
            <option value="DELETE">DELETE</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-error-800 dark:text-error-400">{error}</p>
        </div>
      )}

      {loading && logs.length === 0 ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-600 dark:text-gray-400">No webhook logs found</p>
        </div>
      ) : (
        <>
          <div className="space-y-2 mb-4">
            {logs.map((log) => (
              <div
                key={log._id}
                className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden"
              >
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  onClick={() => toggleExpanded(log._id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      {/* Integration Logo */}
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 flex-shrink-0">
                        <img 
                          src="/xero.svg" 
                          alt="Xero" 
                          className="w-7 h-7"
                          title="Xero Integration"
                        />
                      </div>

                      {/* Status Icon */}
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                        log.error 
                          ? 'bg-error-100 dark:bg-error-900/30' 
                          : 'bg-success-100 dark:bg-success-900/30'
                      }`}>
                        {log.error ? (
                          <AlertCircle className="w-4 h-4 text-error-600 dark:text-error-400" />
                        ) : (
                          <CheckCircle className="w-4 h-4 text-success-600 dark:text-success-400" />
                        )}
                      </div>

                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getEventBadgeColor(log.eventCategory)}`}>
                            {log.eventCategory}
                          </span>
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            {log.eventType}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-500">
                            • {log.resourceId.substring(0, 8)}...
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400">
                          <span>Account: {log.accountId.name}</span>
                          <span>•</span>
                          <span title={`UTC: ${formatDateWithUTC(log.createdAt).utcTime}`}>
                            {formatDateWithUTC(log.createdAt).localTime}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="text-gray-400 dark:text-gray-500">
                      {expandedLog === log._id ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </div>
                  </div>
                </div>

                {expandedLog === log._id && (
                  <div className="border-t border-gray-200 dark:border-gray-600 p-4 bg-gray-50 dark:bg-gray-900">
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">
                          Tenant ID
                        </label>
                        <code className="text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded text-gray-800 dark:text-gray-200 block">
                          {log.tenantId}
                        </code>
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">
                          Resource ID
                        </label>
                        <code className="text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded text-gray-800 dark:text-gray-200 block">
                          {log.resourceId}
                        </code>
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">
                          Event Date
                        </label>
                        <div className="space-y-1">
                          <code className="text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded text-gray-800 dark:text-gray-200 block">
                            <span className="text-gray-500 dark:text-gray-400">Local: </span>
                            {formatDateWithUTC(log.eventDateUtc).localTime}
                          </code>
                          <code className="text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded text-gray-600 dark:text-gray-400 block">
                            <span className="text-gray-500 dark:text-gray-500">UTC: </span>
                            {formatDateWithUTC(log.eventDateUtc).utcTime}
                          </code>
                        </div>
                      </div>

                      {log.error && (
                        <div>
                          <label className="text-xs font-semibold text-error-600 dark:text-error-400 block mb-1">
                            Error
                          </label>
                          <code className="text-xs bg-red-50 dark:bg-gray-700 dark:border dark:border-error-800/50 px-2 py-1 rounded text-error-800 dark:text-error-300 block">
                            {log.error}
                          </code>
                        </div>
                      )}

                      <div>
                        <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">
                          Payload
                        </label>
                        <pre className="text-xs bg-white dark:bg-gray-800 p-3 rounded text-gray-800 dark:text-gray-200 overflow-x-auto">
                          {JSON.stringify(log.payload, null, 2)}
                        </pre>
                      </div>

                      <div>
                        <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 block mb-1">
                          Signature
                        </label>
                        <code className="text-xs bg-white dark:bg-gray-800 px-2 py-1 rounded text-gray-800 dark:text-gray-200 block break-all">
                          {log.signature}
                        </code>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-600">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {pagination.offset + 1}-{Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => fetchLogs(Math.max(0, pagination.offset - pagination.limit))}
                disabled={pagination.offset === 0}
                className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Previous
              </button>
              <button
                onClick={() => fetchLogs(pagination.offset + pagination.limit)}
                disabled={!pagination.hasMore}
                className="px-3 py-1 text-sm bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

